/**
 * Per-line OWN state evaluation (W1-05a engine-eval; rpe-format.md F§4, F§6.2, F§8, F§9, F§10, R§2.2, R§3,
 * DESIGN.md §6.2, §6.7, §6.13). Father composition happens afterwards in compose.ts.
 *
 * A `LineEval` is compiled once per lowered line (`compileLineEval`) and evaluated at any t (`evalOwn`):
 *  - alpha: the alpha chain with the f32 factor 1/255 per keyframe, right-associative layer sum; 0 when the line
 *    has no alpha list (an invisible line whose notes still draw). `alpha < 0` (strictly) hides the line AND its
 *    notes; the -2.98e-8 of layers 68 / -223 / 155 is negative, so those notes are hidden. Before res.alpha.
 *  - own transform: x = Σ moveX·2/1350, y = (Σ moveY·2/900) / ar (f32, divided where used), rotation =
 *    -Σ rotate (world degrees, counter-clockwise).
 *  - scale (F§9.1): factor 1 on `line.png` lines, 2/1350 on textured lines, per keyframe; scaleX on a `line.png`
 *    line is additionally x0.5 unless the line has a NON-EMPTY textEvents list (typed or written back in
 *    `ignoredLists`; paint lines included) or an attachUI. No list -> the default (1, or 2/1350 for textures),
 *    never halved. `scaleYRaw` is the raw scaleY value (thickness quirk |scaleY - 1| > 1e-4), null when absent.
 *  - colour from colorEvents (0..1, white before a first event after beat 0), null without colour events (the
 *    renderer applies the default colour: respack Perfect colour on normal lines, white elsewhere).
 *  - text (text lines): Phira's string tween, the raw end string after the last keyframe (textEval.ts).
 *  - incline: FROZEN at the value of a never-advanced track at t = 0 (Phira never calls set_time on it); stored as
 *    sin(incline) for the note x squeeze.
 *  - gif progress (GIF lines): the natural-speed loop track, reported as the loop phase in [0, 1) so that
 *    `(gifProgress * totalMs) mod totalMs` (= `gifMsFromPhase`) is exactly Phira's frame position.
 *  - paint (paint lines): the brush value (-1 before a first event after beat 0).
 *  - drawsNothing: 'uiMover' (attachUI: Phira hides the line and its notes), 'alpha' (opacity <= 0), 'scale'
 *    (a zero / NaN scale), 'emptyText' (a text line showing "") or null. Paint lines (R§3.7) have no body and their
 *    persistent canvas is composited full-viewport whatever the line's opacity or scale, so earlier stamps may stay
 *    visible: they report 'paintCleared' only while the canvas is CLEARED (brush <= 0), when nothing of the line
 *    shows, else null (CR-W1-05a-3).
 *
 * Kind (F§9.5): `line.png` -> paint if paintEvents exist, else text if textEvents exist, else normal; any other
 * texture -> gif if gifEvents exist (even `[]`), else texture (a .gif without gifEvents shows its first frame).
 *
 * Internal API (W1-05b engine-graph):
 *   compileLineEval(ir, env, opts) -> LineEval      once per lowered line (recompile on IR / registry change)
 *   lineEval.setFloor(maxTime, speed)                build the floor curve (again on max_time / speed-mode change)
 *   lineEval.floorFor(muted)                         the floor without muted speed layers (cached per combination)
 *   buildLineFloor(ir, env, maxTime, speed, muted?)  the floor builder itself (legacy or 170 by speed mode)
 *   lineEval.evalOwn(t, ar, muted, out)              own state into a pooled LineStateBuf
 *   lineEval.displayValue(prop, t, layer)            channelValue readouts in RPE units
 *   lineEval.activeEvent(prop, layer, t)             IR event active at t (segmentAt)
 *   lineEval.controls.at(which, h)                   note control factor (EngineCompileContext.control)
 *   new LineStateBuf(), lineKindOf, scaleRule, gifMsFromPhase
 */

import type { IrEventList, IrLayer, IrLine } from '../ir/types';
import type { ExtProp, LayeredProp, LineId, LineKind, LineProp, SpeedMode } from '../model/types';
import type { MutMat2D, RGB } from '../util/types';
import { AnimF32, ColorAnim, GifTrack, gifPhase, type EvalEnv } from './anim';
import { extendedOf, ignoredList, lineUsesBezier } from './bezierRegistry';
import { compileLayerChain, type LayerChain, muteKey } from './channels';
import { compileControls, type LineControls } from './controls';
import { zeroFloor, type FloorCurve } from './floor/curve';
import { buildLegacyFloor } from './floor/legacy';
import { buildRpe170Floor, rpe170Mode } from './floor/rpe170';
import { TextTrack } from './textEval';
import type { LineStateRt } from './types';

const fr = Math.fround;

/** The built-in line texture name (exact string). */
export const BUILTIN_LINE_TEXTURE = 'line.png';
/** Texture scale factor and default: 2/1350 per texture pixel, f32. */
export const TEXTURE_SCALE_F32 = fr(2 / 1350);
/** Phira's f32 `to_radians` constant: `PI_f32 / 180` in f32. */
export const DEG_TO_RAD_F32 = fr(fr(Math.PI) / 180);

const LAYERED: readonly LayeredProp[] = ['alpha', 'moveX', 'moveY', 'rotate', 'speed'];

/** Speed layers whose mute combination fits a positive 31-bit integer mask (`floorFor`). */
const MASK_KEYS = 30;

function isLayered(prop: LineProp): prop is LayeredProp {
  return (LAYERED as readonly string[]).includes(prop);
}

// ---------------------------------------------------------------------------------------------------------------
// Pooled state
// ---------------------------------------------------------------------------------------------------------------

/** Mutable, pooled implementation of LineStateRt (overwritten by every evaluation). */
export class LineStateBuf implements LineStateRt {
  line: LineId;
  index: number;
  kind: LineKind = 'normal';
  uiMover = false;
  alpha = 0;
  ownX = 0;
  ownY = 0;
  ownRot = 0;
  x = 0;
  y = 0;
  rot = 0;
  sx = 1;
  sy = 1;
  scaleYRaw: number | null = null;
  color: RGB | null = null;
  text: string | null = null;
  height = 0;
  inclineSin = 0;
  gifProgress = Number.NaN;
  paint = Number.NaN;
  drawsNothing: LineStateRt['drawsNothing'] = null;
  readonly m: MutMat2D = [1, 0, 0, 1, 0, 0];
  /** Backing store of `color` (reused). */
  readonly colorBuf: [number, number, number] = [1, 1, 1];

  constructor(line: LineId = 0 as LineId, index = 0) {
    this.line = line;
    this.index = index;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Kind and scale rules
// ---------------------------------------------------------------------------------------------------------------

/** Phira's line kind (F§9.5). */
export function lineKindOf(ir: Pick<IrLine, 'Texture' | 'extended'>): LineKind {
  const ext = ir.extended === 'absent' || ir.extended === null ? null : ir.extended;
  if (ir.Texture === BUILTIN_LINE_TEXTURE) {
    if (ext?.paintEvents !== undefined) return 'paint';
    if (ext?.textEvents !== undefined) return 'text';
    return 'normal';
  }
  return ext?.gifEvents !== undefined ? 'gif' : 'texture';
}

/** True when the line has a non-empty textEvents list, typed or written back in `ignoredLists`. */
export function hasNonEmptyText(ir: IrLine): boolean {
  const ext = extendedOf(ir);
  if (ext === null) return false;
  if (ext.textEvents !== undefined && ext.textEvents.length > 0) return true;
  const raw = ignoredList(ext, 'textEvents');
  return raw !== null && raw.length > 0;
}

export interface ScaleRule {
  /** Per-keyframe factors of scaleX / scaleY events. */
  readonly factorX: number;
  readonly factorY: number;
  /** Value used when the list is absent (never halved). */
  readonly defaultValue: number;
  /** The x0.5 scaleX rule applies. */
  readonly half: boolean;
}

/** F§9.1 scale factors of a line. */
export function scaleRule(ir: IrLine): ScaleRule {
  if (ir.Texture !== BUILTIN_LINE_TEXTURE) {
    return { factorX: TEXTURE_SCALE_F32, factorY: TEXTURE_SCALE_F32, defaultValue: TEXTURE_SCALE_F32, half: false };
  }
  const half = !hasNonEmptyText(ir) && ir.attachUI === null;
  return { factorX: half ? 0.5 : 1, factorY: 1, defaultValue: 1, half };
}

/**
 * drawsNothing of a (non-UI-mover) paint line: Phira clears the persistent canvas exactly when the brush is <= 0
 * (R§3.7), and only then is nothing of the line visible; with a positive brush (or NaN: neither stamp nor clear)
 * the canvas keeps its earlier stamps on screen even when the new stamp is transparent or zero-sized.
 */
export function paintDrawsNothing(paint: number): LineStateRt['drawsNothing'] {
  return paint <= 0 ? 'paintCleared' : null;
}

/** Phira's GIF frame position (ms) from the loop phase stored in LineStateRt.gifProgress. */
export function gifMsFromPhase(phase: number, totalMs: number): number {
  if (!(totalMs > 0) || Number.isNaN(phase) || phase <= 0) return 0;
  return Math.floor(phase * totalMs) % totalMs;
}

// ---------------------------------------------------------------------------------------------------------------
// Compiled line
// ---------------------------------------------------------------------------------------------------------------

export interface LineEvalOptions {
  /** Id reported in LineStateRt (default: `ir.srcLine`, else the index). */
  readonly line?: LineId;
  /** Export index. */
  readonly index: number;
  /** Total loop duration of the line's GIF texture in ms (GIF lines); without it gifProgress is NaN. */
  readonly gifTotalMs?: number | null;
}

/** Which event of a list is active at t: its index and whether t lies after its end (gap / after the last). */
export interface ActiveEvent {
  readonly event: number;
  readonly atEnd: boolean;
}

function activeOf(anim: AnimF32, t: number): ActiveEvent | null {
  const tl = anim.timeline;
  if (tl.n === 0 || !(t >= (tl.times[0] ?? Number.NaN))) return null;
  const i = tl.cursorAt(t);
  const ev = tl.eventOf(i);
  if (ev < 0) return null;
  return { event: ev, atEnd: !tl.isEventStart(i) };
}

export class LineEval {
  readonly ir: IrLine;
  readonly env: EvalEnv;
  readonly line: LineId;
  readonly index: number;
  readonly kind: LineKind;
  readonly uiMover: boolean;
  /** Export index of the parent, or -1. */
  readonly father: number;
  readonly rotateWithFather: boolean;
  readonly scale: ScaleRule;
  readonly alpha: LayerChain;
  readonly moveX: LayerChain;
  readonly moveY: LayerChain;
  readonly rotate: LayerChain;
  readonly scaleX: AnimF32 | null;
  readonly scaleY: AnimF32 | null;
  readonly color: ColorAnim | null;
  /** Text track (text lines only). */
  readonly text: TextTrack | null;
  /** Brush track (paint lines only). */
  readonly paint: AnimF32 | null;
  /** GIF progress track (GIF lines with a known loop duration). */
  readonly gif: GifTrack | null;
  /** Incline frozen at t = 0 (degrees, f32) and its sine. */
  readonly inclineDeg: number;
  readonly inclineSin: number;
  readonly controls: LineControls;
  /** Any event of the line uses a bezier curve (recompile when the global bezier registry changes). */
  readonly usesBezier: boolean;
  /** Floor curve (`setFloor(maxTime, speed)` or assign `buildLineFloor(...)`; H = 0 until then). */
  floor: FloorCurve = zeroFloor(1);
  /** Speed-layer mute keys of this line (`speed:<slot>`), for filtered floors. */
  readonly speedMuteKeys: readonly string[];

  private floorParams: { readonly maxTime: number; readonly speed: SpeedSettings } | null = null;
  /** Filtered floors by mute combination: a bit mask over `speedMuteKeys` (a string key past 30 speed layers). */
  private readonly mutedFloors = new Map<number | string, FloorCurve>();

  private scaleYRawAnim: AnimF32 | null | undefined = undefined;
  private readonly displayChains = new Map<LayeredProp, LayerChain>();
  private readonly displayExt = new Map<ExtProp, AnimF32 | null>();
  private readonly probes = new Map<'color' | 'text', AnimF32>();

  constructor(ir: IrLine, env: EvalEnv, opts: LineEvalOptions) {
    this.ir = ir;
    this.env = env;
    this.index = opts.index;
    this.line = opts.line ?? ir.srcLine ?? (opts.index as LineId);
    this.kind = lineKindOf(ir);
    this.uiMover = ir.attachUI !== null;
    this.father = ir.father;
    this.rotateWithFather = ir.rotateWithFather;
    this.scale = scaleRule(ir);
    const layers = ir.eventLayers;
    this.alpha = compileLayerChain(layers, 'alpha', env);
    this.moveX = compileLayerChain(layers, 'moveX', env);
    this.moveY = compileLayerChain(layers, 'moveY', env);
    this.rotate = compileLayerChain(layers, 'rotate', env);
    const ext = extendedOf(ir);
    this.scaleX =
      ext?.scaleXEvents === undefined
        ? null
        : AnimF32.fromEvents(ext.scaleXEvents, env, { factor: this.scale.factorX });
    this.scaleY =
      ext?.scaleYEvents === undefined
        ? null
        : AnimF32.fromEvents(ext.scaleYEvents, env, { factor: this.scale.factorY });
    this.color = ext?.colorEvents === undefined ? null : ColorAnim.fromEvents(ext.colorEvents, env);
    this.text =
      this.kind === 'text' && ext?.textEvents !== undefined ? TextTrack.fromEvents(ext.textEvents, env) : null;
    this.paint =
      this.kind === 'paint' && ext?.paintEvents !== undefined
        ? AnimF32.fromEvents(ext.paintEvents, env, { default: -1 })
        : null;
    const total = opts.gifTotalMs ?? null;
    this.gif =
      this.kind === 'gif' && ext?.gifEvents !== undefined && total !== null && total > 0
        ? new GifTrack(ext.gifEvents, env, total)
        : null;
    const incline =
      ext?.inclineEvents === undefined ? 0 : AnimF32.fromEvents(ext.inclineEvents, env, { default: 0 }).valueAtLoad();
    this.inclineDeg = incline;
    this.inclineSin = fr(Math.sin(fr(incline * DEG_TO_RAD_F32)));
    this.controls = compileControls(ir);
    this.usesBezier = lineUsesBezier(ir);
    const speedKeys: string[] = [];
    layers.forEach((layer, slot) => {
      if (layer?.speedEvents !== undefined) speedKeys.push(muteKey('speed', slot));
    });
    this.speedMuteKeys = speedKeys;
  }

  /** Builds and assigns the floor curve for max_time and the package speed settings (filtered floors reset). */
  setFloor(maxTime: number, speed: SpeedSettings): FloorCurve {
    this.floorParams = { maxTime, speed };
    this.mutedFloors.clear();
    this.floor = buildLineFloor(this.ir, this.env, maxTime, speed);
    return this.floor;
  }

  /**
   * The floor under the preview mutes: the plain floor unless a speed layer of this line is muted, then a floor
   * built without the muted layers (cached per mute combination; needs `setFloor`). Called per line per frame, so
   * the lookup allocates nothing: the combination is a bit mask over `speedMuteKeys` (recomputed on every call, so
   * a set mutated in place is still read correctly).
   */
  floorFor(muted: ReadonlySet<string> | null | undefined): FloorCurve {
    if (muted === null || muted === undefined || muted.size === 0 || this.floorParams === null) return this.floor;
    const keys = this.speedMuteKeys;
    let combo: number | string;
    if (keys.length <= MASK_KEYS) {
      let mask = 0;
      for (let j = 0; j < keys.length; j++) if (muted.has(keys[j] ?? '')) mask |= 1 << j;
      if (mask === 0) return this.floor;
      combo = mask;
    } else {
      let key = '';
      for (const k of keys) if (muted.has(k)) key += `${k};`;
      if (key === '') return this.floor;
      combo = key;
    }
    let f = this.mutedFloors.get(combo);
    if (f === undefined) {
      f = buildLineFloor(this.ir, this.env, this.floorParams.maxTime, this.floorParams.speed, muted);
      this.mutedFloors.set(combo, f);
    }
    return f;
  }

  /** Raw (factor 1) scaleY at t, or null when the line has no scaleY list. */
  scaleYRawAt(t: number, scaled: number): number | null {
    if (this.scaleY === null) return null;
    if (this.scale.factorY === 1) return scaled;
    if (this.scaleYRawAnim === undefined) {
      const ext = extendedOf(this.ir);
      this.scaleYRawAnim = ext?.scaleYEvents === undefined ? null : AnimF32.fromEvents(ext.scaleYEvents, this.env);
    }
    return this.scaleYRawAnim === null ? null : this.scaleYRawAnim.valueAt(t);
  }

  /**
   * Own state at t into `out` (x / y / rot / m are left for compose.ts). `ar` is the aspect ratio the moveY
   * division uses (the frame viewport's `ar`); `muted` holds this line's muted `${prop}:${slot}` keys.
   */
  evalOwn(t: number, ar: number, muted: ReadonlySet<string> | null | undefined, out: LineStateBuf): LineStateBuf {
    out.line = this.line;
    out.index = this.index;
    out.kind = this.kind;
    out.uiMover = this.uiMover;
    const alpha = this.alpha.valueAt(t, muted);
    out.alpha = alpha;
    out.ownX = this.moveX.valueAt(t, muted);
    out.ownY = fr(this.moveY.valueAt(t, muted) / fr(ar));
    out.ownRot = this.rotate.valueAt(t, muted);
    const sx = this.scaleX === null ? this.scale.defaultValue : this.scaleX.valueAt(t);
    const sy = this.scaleY === null ? this.scale.defaultValue : this.scaleY.valueAt(t);
    out.sx = sx;
    out.sy = sy;
    out.scaleYRaw = this.scaleYRawAt(t, sy);
    out.color = this.color === null ? null : this.color.at(t, out.colorBuf);
    const text = this.text === null ? null : this.text.at(t);
    out.text = text;
    out.height = this.floorFor(muted).heightAt(t);
    out.inclineSin = this.inclineSin;
    out.gifProgress = this.gif === null ? Number.NaN : gifPhase(this.gif.progressAt(t));
    const paint = this.paint === null ? Number.NaN : this.paint.valueAt(t);
    out.paint = paint;
    out.drawsNothing = this.drawsNothingOf(alpha, sx, sy, text, paint);
    return out;
  }

  /** Why Phira draws nothing for this line given its evaluated own values (see the module comment). */
  private drawsNothingOf(
    alpha: number,
    sx: number,
    sy: number,
    text: string | null,
    paint: number,
  ): LineStateRt['drawsNothing'] {
    if (this.uiMover) return 'uiMover';
    if (this.kind === 'paint') {
      return this.paint === null ? 'alpha' : paintDrawsNothing(paint);
    }
    if (!(alpha > 0)) return 'alpha';
    if (!(sx !== 0 && sy !== 0 && !Number.isNaN(sx) && !Number.isNaN(sy))) return 'scale';
    if (this.kind === 'text' && text === '') return 'emptyText';
    return null;
  }

  /** Unwrapped GIF progress at t as Phira's track yields it (NaN without a GIF track). */
  gifProgressAt(t: number): number {
    return this.gif === null ? Number.NaN : this.gif.progressAt(t);
  }

  /** GIF loop position in ms at t (Phira's frame lookup); 0 without a GIF track. */
  gifMsAt(t: number): number {
    return this.gif === null ? 0 : this.gif.msAt(t);
  }

  private displayChain(prop: LayeredProp): LayerChain {
    let c = this.displayChains.get(prop);
    if (c === undefined) {
      c = compileLayerChain(this.ir.eventLayers, prop, this.env, 1);
      this.displayChains.set(prop, c);
    }
    return c;
  }

  private extList(prop: ExtProp): IrEventList<number> | undefined {
    const ext = extendedOf(this.ir);
    if (ext === null) return undefined;
    switch (prop) {
      case 'scaleX':
        return ext.scaleXEvents;
      case 'scaleY':
        return ext.scaleYEvents;
      case 'incline':
        return ext.inclineEvents;
      case 'paint':
        return ext.paintEvents;
      case 'gif':
        return ext.gifEvents === undefined || ext.gifEvents.length === 0
          ? undefined
          : (ext.gifEvents as IrEventList<number>);
      case 'color':
      case 'text':
        return undefined;
    }
  }

  private displayExtAnim(prop: ExtProp): AnimF32 | null {
    if (this.displayExt.has(prop)) return this.displayExt.get(prop) ?? null;
    const list = this.extList(prop);
    const def = prop === 'incline' ? 0 : prop === 'paint' ? -1 : undefined;
    const anim =
      list === undefined ? null : AnimF32.fromEvents(list, this.env, def === undefined ? {} : { default: def });
    this.displayExt.set(prop, anim);
    return anim;
  }

  /**
   * Readout value in RPE units (`Engine.channelValue`): summed properties per slot or 'sum' (alpha 0..255, px,
   * clockwise degrees, RPE speed); scaleX / scaleY raw event values; incline / paint with their Phira defaults;
   * gif the unwrapped progress (natural play included when the loop duration is known). NaN when absent and for
   * colour / text.
   */
  displayValue(prop: LineProp, t: number, layer: number | 'sum' = 'sum'): number {
    if (isLayered(prop)) return this.displayChain(prop).displayAt(t, layer);
    if (layer !== 'sum' && layer !== 0) return Number.NaN;
    if (prop === 'gif' && this.gif !== null) return this.gif.progressAt(t);
    const anim = this.displayExtAnim(prop);
    return anim === null ? Number.NaN : anim.valueAt(t);
  }

  /** The IR event of (prop, layer) active at t, or null before its first keyframe / when the list is absent. */
  activeEvent(prop: LineProp, layer: number, t: number): ActiveEvent | null {
    if (isLayered(prop)) {
      const c = this.displayChain(prop).cursorOf(layer, t);
      return c === null ? null : activeOf(c.anim, t);
    }
    if (layer !== 0) return null;
    const ext = extendedOf(this.ir);
    if (ext === null) return null;
    if (prop === 'color' || prop === 'text') {
      const list = prop === 'color' ? ext.colorEvents : ext.textEvents;
      if (list === undefined) return null;
      let probe = this.probes.get(prop);
      if (probe === undefined) {
        probe = probeTrack(list, this.env);
        this.probes.set(prop, probe);
      }
      return activeOf(probe, t);
    }
    const anim = this.displayExtAnim(prop);
    return anim === null ? null : activeOf(anim, t);
  }
}

/** A numeric probe track with the same keyframe times / sources as a colour or text list (segment lookups only). */
function probeTrack(events: IrEventList<unknown>, env: EvalEnv): AnimF32 {
  return AnimF32.fromEvents(
    events.map((e) => ({ ...e, start: 0, end: 0 })),
    env,
    { default: 0 },
  );
}

/** Compiles a lowered line. */
export function compileLineEval(ir: IrLine, env: EvalEnv, opts: LineEvalOptions): LineEval {
  return new LineEval(ir, env, opts);
}

// ---------------------------------------------------------------------------------------------------------------
// Floor dispatch
// ---------------------------------------------------------------------------------------------------------------

export interface SpeedSettings {
  /** pkg.speedMode ('legacy' = Phira default integrator, 'rpe170' = useRpe170Speed). */
  readonly mode: SpeedMode;
  /** META.RPEVersion as Phira computes it (>= 170 -> Modern sub-mode of the 170 integrator). */
  readonly rpeVersionEffective: number;
}

/**
 * The floor curve of a line for the package's speed mode. `mutedSlots` (preview filter) leaves muted speed layers
 * out, exactly as if the slot had no speed list.
 */
export function buildLineFloor(
  ir: Pick<IrLine, 'eventLayers'>,
  env: EvalEnv,
  maxTime: number,
  speed: SpeedSettings,
  muted?: ReadonlySet<string> | null,
): FloorCurve {
  let layers: readonly (IrLayer | null)[] = ir.eventLayers;
  if (muted !== undefined && muted !== null && muted.size > 0) {
    layers = layers.map((l, slot) => (l !== null && muted.has(muteKey('speed', slot)) ? null : l));
  }
  return speed.mode === 'rpe170'
    ? buildRpe170Floor(layers, env, maxTime, rpe170Mode(speed.rpeVersionEffective))
    : buildLegacyFloor(layers, env, maxTime);
}
